import {
    AUTOCOMPLETE_CONFIG,
    CETIP_AUTOCOMPLETE_ACTIONS,
    ERROR_MESSAGES,
    INITIAL_STATE_CETIP_ISIN,
} from "../constants/use-reducer-auto-complete-input-values.const"
import {useCallback, useMemo, useReducer, useRef} from "react"

const createHandlers = () => ({
    [CETIP_AUTOCOMPLETE_ACTIONS.SET_INPUT_VALUE]: (state, payload) => ({
        ...state,
        inputValue: payload || "",
        error: null,
    }),

    [CETIP_AUTOCOMPLETE_ACTIONS.SET_SELECTED_VALUE]: (state, payload) => ({
        ...state,
        selectedValue: payload,
        error: null,
    }),

    [CETIP_AUTOCOMPLETE_ACTIONS.SEARCH_START]: (state) => ({
        ...state,
        loading: true,
        error: null,
    }),

    [CETIP_AUTOCOMPLETE_ACTIONS.SEARCH_SUCCESS]: (state, payload) => ({
        ...state,
        results: Array.isArray(payload)
            ? payload.slice(0, AUTOCOMPLETE_CONFIG.MAX_RESULTS)
            : [],
        loading: false,
        error: null,
    }),

    [CETIP_AUTOCOMPLETE_ACTIONS.SEARCH_ERROR]: (state, payload) => ({
        ...state,
        results: [],
        loading: false,
        error: payload || ERROR_MESSAGES.SEARCH_FAILED,
    }),

    [CETIP_AUTOCOMPLETE_ACTIONS.SEARCH_CLEAR]: (state) => ({
        ...state,
        results: [],
        loading: false,
        error: null,
    }),

    [CETIP_AUTOCOMPLETE_ACTIONS.RESET_STATE]: () => INITIAL_STATE_CETIP_ISIN,
})

const cetipAutocompleteReducer = (state, action) => {
    const handlers = createHandlers()
    const handler = handlers[action.type]
    return handler ? handler(state, action.payload) : state
}

export const useCetipAutocomplete = (emissaoService) => {
    const [state, dispatch] = useReducer(
        cetipAutocompleteReducer,
        INITIAL_STATE_CETIP_ISIN
    )
    const debounceRef = useRef(null)
    const lastInputValueRef = useRef("")

    const actions = useMemo(
        () => ({
            setInputValue: (value) =>
                dispatch({
                    type: CETIP_AUTOCOMPLETE_ACTIONS.SET_INPUT_VALUE,
                    payload: value,
                }),

            setSelectedValue: (value) =>
                dispatch({
                    type: CETIP_AUTOCOMPLETE_ACTIONS.SET_SELECTED_VALUE,
                    payload: value,
                }),

            startSearch: () =>
                dispatch({type: CETIP_AUTOCOMPLETE_ACTIONS.SEARCH_START}),

            searchSuccess: (results) =>
                dispatch({
                    type: CETIP_AUTOCOMPLETE_ACTIONS.SEARCH_SUCCESS,
                    payload: results,
                }),

            searchError: (errorMessage) =>
                dispatch({
                    type: CETIP_AUTOCOMPLETE_ACTIONS.SEARCH_ERROR,
                    payload: errorMessage,
                }),

            clearSearch: () =>
                dispatch({type: CETIP_AUTOCOMPLETE_ACTIONS.SEARCH_CLEAR}),

            resetState: () =>
                dispatch({type: CETIP_AUTOCOMPLETE_ACTIONS.RESET_STATE}),
        }),
        []
    )

    const searchCetipIsin = useCallback(
        async (value = "") => {
            try {
                const emissaoServiceData = await emissaoService.loadEmissoes(value)
                const {body: {results}} = emissaoServiceData
                return Array.from(
                    new Set(results.flatMap((r) => [r.CETIP, r.ISIN]))
                ).filter(Boolean)
            } catch (error) {
                console.error("Erro na busca:", error)
                throw error
            }
        },
        [emissaoService]
    )

    const handleInputChange = useCallback(
        (event, newInputValue) => {
            if (newInputValue === lastInputValueRef.current) return

            lastInputValueRef.current = newInputValue
            actions.setInputValue(newInputValue || "")

            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
            }

            if (
                !newInputValue ||
                newInputValue.length < AUTOCOMPLETE_CONFIG.MIN_SEARCH_LENGTH
            ) {
                actions.clearSearch()
                return
            }

            debounceRef.current = setTimeout(async () => {
                actions.startSearch()

                try {
                    const results = await searchCetipIsin(newInputValue)
                    actions.searchSuccess(results)
                } catch (error) {
                    actions.searchError(
                        error.message || ERROR_MESSAGES.SEARCH_FAILED
                    )
                }
            }, AUTOCOMPLETE_CONFIG.DEBOUNCE_DELAY)
        },
        [actions, searchCetipIsin]
    )

    const handleSelectionChange = useCallback(
        (event, newValue, reason) => {
            actions.setSelectedValue(newValue)

            if (newValue && typeof newValue === "string") {
                lastInputValueRef.current = newValue
                actions.setInputValue(newValue)
            } else if (newValue === null && reason === "clear") {
                lastInputValueRef.current = ""
                actions.setInputValue("")
            }
        },
        [actions]
    )

    const cleanup = useCallback(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
        }
        actions.resetState()
    }, [actions])

    const computed = useMemo(
        () => ({
            hasResults: state.results.length > 0,
            hasError: Boolean(state.error),
            isEmpty: !state.inputValue && !state.selectedValue,
            isSearching: state.loading,
        }),
        [state]
    )

    return {
        state,
        actions,
        handlers: {
            onInputChange: handleInputChange,
            onSelectionChange: handleSelectionChange,
            setInputValue: (newValue) => actions.setInputValue(newValue),
        },
        computed,
        cleanup,
    }
}
